import React from "react";
import { Modal, Button } from "react-bootstrap";
import { BsPersonCheck } from "react-icons/bs";

interface OnlineUser {
  id: string;
  name: string;
  role?: string;
}

interface AssignSystemModalProps {
  show: boolean;
  systemId: number | null;
  systemName?: string;
  currentClaimedBy?: string | null;
  onlineUsers: OnlineUser[];
  onAssign: (systemId: number, userId: string) => void;
  onClose: () => void;
}

// Admin-only picker. Lists only currently-online users so admins can't
// hand a system to someone who isn't around to actually take it. The list
// comes from the same `onlineUsers` socket broadcast the navbar already
// uses — no extra fetch needed.
const AssignSystemModal: React.FC<AssignSystemModalProps> = ({
  show,
  systemId,
  systemName,
  currentClaimedBy,
  onlineUsers,
  onAssign,
  onClose,
}) => {
  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          Assign {systemName ? systemName : "system"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "14px" }}>
          Pick an online user to give this system to. They'll see it appear in
          their claimed list immediately.
        </div>

        {onlineUsers.length === 0 && (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#9ca3af",
              background: "#f9fafb",
              borderRadius: "8px",
              fontSize: "13px",
            }}
          >
            No users are online right now.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "320px", overflowY: "auto" }}>
          {onlineUsers.map((u) => {
            const isCurrent = currentClaimedBy === u.id;
            return (
              <button
                key={u.id}
                onClick={() => systemId !== null && onAssign(systemId, u.id)}
                disabled={isCurrent}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  background: isCurrent ? "#f3f4f6" : "#ffffff",
                  cursor: isCurrent ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  color: "#111827",
                  fontWeight: 500,
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isCurrent) e.currentTarget.style.background = "#eff6ff";
                }}
                onMouseLeave={(e) => {
                  if (!isCurrent) e.currentTarget.style.background = "#ffffff";
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: "#10b981",
                      flexShrink: 0,
                    }}
                  />
                  {u.name}
                  {(u.role === "admin" || u.role === "superadmin") && (
                    <span
                      style={{
                        fontSize: "10px",
                        padding: "2px 6px",
                        background: "#dbeafe",
                        color: "#1e40af",
                        borderRadius: "4px",
                        fontWeight: 600,
                      }}
                    >
                      ADMIN
                    </span>
                  )}
                </span>
                {isCurrent ? (
                  <span style={{ fontSize: "11px", color: "#6b7280" }}>Currently has it</span>
                ) : (
                  <BsPersonCheck size={16} color="#2563eb" />
                )}
              </button>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignSystemModal;
